import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DuoActivity } from '@/types/dinoDuos';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BookOpen, MessageSquare, Trophy, UserPlus, Sparkles } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface DuoActivityFeedProps {
  duoId: string;
}

export function DuoActivityFeed({ duoId }: DuoActivityFeedProps) {
  const [activities, setActivities] = useState<DuoActivity[]>([]);

  useEffect(() => {
    if (!duoId) return;

    fetchActivities();
    setupRealtimeSubscription();
  }, [duoId]);

  const fetchActivities = async () => {
    try {
      // Fetch progress updates
      const { data: progressData, error: progressError } = await supabase
        .from('buddy_read_members')
        .select(`
          id,
          current_page,
          last_updated,
          user:profiles(id, username, name, image)
        `)
        .eq('buddy_read_id', duoId)
        .eq('status', 'accepted')
        .order('last_updated', { ascending: false })
        .limit(20);

      if (progressError) throw progressError;

      // Fetch messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('duo_messages')
        .select(`
          id,
          created_at,
          message_text,
          user:profiles(id, username, name, image)
        `)
        .eq('duo_id', duoId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (messagesError) throw messagesError;

      // Fetch check-ins
      const { data: checkInsData, error: checkInsError } = await supabase
        .from('duo_check_ins')
        .select('*')
        .eq('duo_id', duoId)
        .order('created_at', { ascending: false });

      if (checkInsError) throw checkInsError;

      // Combine and sort all activities
      const allActivities: DuoActivity[] = [
        ...progressData?.map((p: any) => ({
          id: p.id,
          type: 'progress' as const,
          user: Array.isArray(p.user) ? p.user[0] : p.user,
          data: { current_page: p.current_page },
          created_at: p.last_updated
        })) || [],
        ...messagesData?.map((m: any) => ({
          id: m.id,
          type: 'message' as const,
          user: Array.isArray(m.user) ? m.user[0] : m.user,
          data: { message: m.message_text },
          created_at: m.created_at
        })) || [],
        ...checkInsData?.map((c: any) => ({
          id: c.id,
          type: 'milestone' as const,
          user: undefined,
          data: { milestone: c.milestone_percent, prompt: c.prompt_text },
          created_at: c.created_at
        })) || []
      ];

      allActivities.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setActivities(allActivities.slice(0, 30));
    } catch (error: any) {
      console.error('Error fetching activities:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`duo-activity-${duoId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'buddy_read_members',
          filter: `buddy_read_id=eq.${duoId}`
        },
        () => {
          fetchActivities();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'duo_messages',
          filter: `duo_id=eq.${duoId}`
        },
        () => {
          fetchActivities();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'duo_check_ins',
          filter: `duo_id=eq.${duoId}`
        },
        () => {
          fetchActivities();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'progress':
        return <BookOpen className="h-4 w-4" />;
      case 'message':
        return <MessageSquare className="h-4 w-4" />;
      case 'milestone':
        return <Trophy className="h-4 w-4" />;
      case 'member_joined':
        return <UserPlus className="h-4 w-4" />;
      default:
        return <Sparkles className="h-4 w-4" />;
    }
  };

  const renderActivity = (activity: DuoActivity) => {
    switch (activity.type) {
      case 'progress':
        return (
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-primary/10 p-2">
              {getActivityIcon('progress')}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={activity.user?.image} />
                  <AvatarFallback>
                    {activity.user?.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm">
                  {activity.user?.name || activity.user?.username}
                </span>
                <span className="text-sm text-muted-foreground">
                  updated progress to page {activity.data.current_page}
                </span>
              </div>
              <span className="text-xs text-muted-foreground ml-8">
                {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
        );

      case 'message':
        return (
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-primary/10 p-2">
              {getActivityIcon('message')}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={activity.user?.image} />
                  <AvatarFallback>
                    {activity.user?.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm">
                  {activity.user?.name || activity.user?.username}
                </span>
                <span className="text-sm text-muted-foreground">sent a message</span>
              </div>
              <p className="text-sm text-muted-foreground ml-8 line-clamp-2">
                "{activity.data.message}"
              </p>
              <span className="text-xs text-muted-foreground ml-8">
                {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
        );

      case 'milestone':
        return (
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-yellow-500/10 p-2">
              {getActivityIcon('milestone')}
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">
                🎉 {activity.data.milestone}% milestone reached!
              </p>
              <p className="text-sm text-muted-foreground">
                New check-in: {activity.data.prompt}
              </p>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Feed</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {activities.map((activity) => (
              <div key={`${activity.type}-${activity.id}`}>
                {renderActivity(activity)}
              </div>
            ))}
            {activities.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No activity yet. Start reading and chatting to see updates here!
              </p>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
